import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Spinner } from '@/components/ui/spinner';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { X, Check, CreditCard, Lock, AlertTriangle } from 'lucide-react';
import { useCartStore } from '@/store/cartStore';
import { useToast } from '@/hooks/use-toast';
import { apiRequest } from '@/lib/queryClient';
import SuccessModal from './SuccessModal';

interface CheckoutProps {
  onClose: () => void;
  onSuccess: () => void;
  initialEmail?: string;
}

// Form schemas
const emailSchema = z.object({
  email: z.string().email('Invalid email address'),
});

const otpSchema = z.object({
  otp: z.string().length(6, 'OTP must be 6 digits'),
});

const addressSchema = z.object({
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  company: z.string().optional(),
  street: z.string().min(1, 'Street address is required'),
  city: z.string().min(1, 'City is required'),
  state: z.string().min(1, 'State is required'),
  postcode: z.string().min(4, 'Valid postcode required'),
  phone: z.string().optional(),
});

const paymentSchema = z.object({
  cardNumber: z.string().min(1, 'Card number is required'),
  expiryDate: z.string().min(1, 'Expiry date is required'),
  cvv: z.string().min(3, 'CVV is required'),
  cardholderName: z.string().min(1, 'Cardholder name is required'),
});

export default function Checkout({ onClose, onSuccess, initialEmail = '' }: CheckoutProps) {
  const [step, setStep] = useState(1);
  const [checkoutData, setCheckoutData] = useState<any>({});
  const [showSuccess, setShowSuccess] = useState(false);
  const [orderResult, setOrderResult] = useState<any>(null);
  const { items, getSubtotal, getGST, getTotal, clearCart } = useCartStore();
  const { toast } = useToast();

  // Form instances
  const emailForm = useForm({
    resolver: zodResolver(emailSchema),
    defaultValues: { email: initialEmail },
  });

  const otpForm = useForm({
    resolver: zodResolver(otpSchema),
    defaultValues: { otp: '' },
  });

  const addressForm = useForm({
    resolver: zodResolver(addressSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      company: '',
      street: '',
      city: '',
      state: '',
      postcode: '',
      phone: '',
    },
  });

  const paymentForm = useForm({
    resolver: zodResolver(paymentSchema),
    defaultValues: {
      cardNumber: '',
      expiryDate: '',
      cvv: '',
      cardholderName: '',
    },
  });

  // Mutations
  const sendOtpMutation = useMutation({
    mutationFn: async (data: { email: string }) => {
      const response = await apiRequest('POST', '/api/auth/send-otp', data);
      return response.json();
    },
    onSuccess: (data) => {
      setCheckoutData((prev: any) => ({ ...prev, email: emailForm.getValues().email }));
      setStep(1.5); // Show OTP step
      toast({
        title: "Verification Code Sent",
        description: `Check your email for the 6-digit code${data.demoOtp ? ` (Demo: ${data.demoOtp})` : ''}`,
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to send verification code",
        variant: "destructive",
      });
    },
  });

  const verifyOtpMutation = useMutation({
    mutationFn: async (data: { email: string; code: string }) => {
      const response = await apiRequest('POST', '/api/auth/verify-otp', data);
      return response.json();
    },
    onSuccess: () => {
      setStep(2);
      toast({
        title: "Email Verified",
        description: "Email verification successful!",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Verification Failed",
        description: error.message || "Invalid verification code",
        variant: "destructive",
      });
    },
  });

  const checkoutMutation = useMutation({
    mutationFn: async (data: any) => {
      const response = await apiRequest('POST', '/api/checkout', data);
      return response.json();
    },
    onSuccess: (data) => {
      setOrderResult(data);
      setShowSuccess(true);
      clearCart();
      toast({
        title: "Payment Successful!",
        description: "Your license keys have been generated and sent to your email.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Payment Failed",
        description: error.message || "Payment processing failed",
        variant: "destructive",
      });
    },
  });

  const handleEmailSubmit = (data: { email: string }) => {
    if (!sendOtpMutation.isPending) {
      sendOtpMutation.mutate(data);
    }
  };

  const handleOtpSubmit = (data: { otp: string }) => {
    if (!verifyOtpMutation.isPending) {
      verifyOtpMutation.mutate({
        email: checkoutData.email,
        code: data.otp,
      });
    }
  };

  const handleAddressSubmit = (data: any) => {
    setCheckoutData((prev: any) => ({ ...prev, billing: data }));
    setStep(3);
  };

  const handlePaymentSubmit = (data: any) => {
    if (!checkoutMutation.isPending) {
      const orderData = {
        email: checkoutData.email,
        billing: checkoutData.billing,
        payment: data,
        items: items.map(item => ({
          productId: item.id,
          quantity: item.quantity,
        })),
      };

      checkoutMutation.mutate(orderData);
    }
  };

  const getStepClass = (stepNumber: number) => {
    if (stepNumber < step || (stepNumber === 1 && step === 1.5)) {
      return 'w-8 h-8 bg-green-500 text-white rounded-full flex items-center justify-center text-sm font-semibold mr-2';
    } else if (stepNumber === step) {
      return 'w-8 h-8 bg-nk-orange text-white rounded-full flex items-center justify-center text-sm font-semibold mr-2';
    } else {
      return 'w-8 h-8 bg-gray-200 text-gray-600 rounded-full flex items-center justify-center text-sm font-semibold mr-2';
    }
  };

  const getStepTextClass = (stepNumber: number) => {
    if (stepNumber < step || (stepNumber === 1 && step === 1.5)) {
      return 'text-sm font-medium text-green-600';
    } else if (stepNumber === step) {
      return 'text-sm font-medium';
    } else {
      return 'text-sm text-gray-600';
    }
  };

  if (showSuccess) {
    return (
      <SuccessModal
        isOpen={showSuccess}
        orderResult={orderResult}
        onClose={() => {
          setShowSuccess(false);
          onSuccess();
        }}
      />
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" data-testid="checkout-modal">
      <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle>Checkout</CardTitle>
          <Button variant="ghost" size="icon" onClick={onClose} disabled={sendOtpMutation.isPending || verifyOtpMutation.isPending || checkoutMutation.isPending}>
            <X className="h-4 w-4" />
          </Button>
        </CardHeader>
        <CardContent>
          {/* Step indicator */}
          <div className="flex items-center mb-6">
            <div className={getStepClass(1)}>
              {step > 1 || step === 1.5 ? <Check className="h-4 w-4" /> : '1'}
            </div>
            <div className="flex-1 h-1 bg-gray-200 mr-2">
              <div className={`h-full bg-nk-orange transition-all ${step >= 2 ? 'w-full' : 'w-0'}`} />
            </div>
            <div className={getStepClass(2)}>
              {step > 2 ? <Check className="h-4 w-4" /> : '2'}
            </div>
            <div className="flex-1 h-1 bg-gray-200 mr-2">
              <div className={`h-full bg-nk-orange transition-all ${step >= 3 ? 'w-full' : 'w-0'}`} />
            </div>
            <div className={getStepClass(3)}>3</div>
          </div>

          {/* Email verification step */}
          {step === 1 && (
            <Form {...emailForm}>
              <form onSubmit={emailForm.handleSubmit(handleEmailSubmit)} className="space-y-4">
                <FormField
                  control={emailForm.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email Address</FormLabel>
                      <FormControl>
                        <Input type="email" {...field} disabled={sendOtpMutation.isPending} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <Button 
                  type="submit" 
                  className="w-full bg-nk-orange"
                  disabled={sendOtpMutation.isPending}
                >
                  {sendOtpMutation.isPending ? (
                    <div className="flex items-center justify-center">
                      <Spinner className="mr-2" size={16} />
                      <span>Sending Code...</span>
                    </div>
                  ) : (
                    'Continue'
                  )}
                </Button>
              </form>
            </Form>
          )}

          {/* OTP verification step */}
          {step === 1.5 && (
            <Form {...otpForm}>
              <form onSubmit={otpForm.handleSubmit(handleOtpSubmit)} className="space-y-4">
                <FormField
                  control={otpForm.control}
                  name="otp"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Verification Code</FormLabel>
                      <FormControl>
                        <Input {...field} maxLength={6} disabled={verifyOtpMutation.isPending} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <Button 
                  type="submit" 
                  className="w-full bg-nk-orange"
                  disabled={verifyOtpMutation.isPending}
                >
                  {verifyOtpMutation.isPending ? (
                    <div className="flex items-center justify-center">
                      <Spinner className="mr-2" size={16} />
                      <span>Verifying...</span>
                    </div>
                  ) : (
                    'Verify'
                  )}
                </Button>
              </form>
            </Form>
          )}

          {/* Payment step */}
          {step === 3 && (
            <Form {...paymentForm}>
              <form onSubmit={paymentForm.handleSubmit(handlePaymentSubmit)} className="space-y-4">
                <Button 
                  type="submit" 
                  className="w-full bg-nk-orange"
                  disabled={checkoutMutation.isPending}
                >
                  {checkoutMutation.isPending ? (
                    <div className="flex items-center justify-center">
                      <Spinner className="mr-2" size={16} />
                      <span>Processing Payment...</span>
                    </div>
                  ) : (
                    'Complete Purchase'
                  )}
                </Button>
              </form>
            </Form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
